import { useId, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from '@/components/ui/sonner';
import { Switch } from '@/components/ui/switch';
import { useSalesAgentSettings, useSaveSalesAgentSettings } from '@/hooks/use-sales-agent-settings';
import { cn, getApiErrorMessage } from '@/lib/utils';
import { PILL_BUTTON } from '../../ui-chrome';
import { draftValues, savedValues, sameValues, toSettingsDraft, type SettingsDraft } from '../settings-draft';
import { DEFAULT_SUBTITLE, SUBTITLE_MAX_LENGTH, type SalesAgentSettings } from '../types';
import { DisclaimerTextField } from './disclaimer-text-field';
import { SettingsFieldCard } from './settings-field-card';
import { StarterQuestionsSettings } from './starter-questions-settings';

export const NEEDS_TEXT_MESSAGE = 'Add the disclaimer text before asking for consent.';

export const SUBTITLE_HELP = `Shown under your brand name in the chat. Leave empty to use “${DEFAULT_SUBTITLE}”.`;

/**
 * The Sales Agent's Settings tab (Figma Kim-AI 6828:14840): the live chat disclaimer (whether
 * shoppers must agree before chatting, and the text they see), the subtitle under the brand name
 * and the conversation starters.
 */
export const SettingsTab = () => {
	const { data, isLoading, isError } = useSalesAgentSettings();

	if (isLoading) return <div className='px-5 pt-6 text-xs text-gray-800'>Loading settings…</div>;
	if (isError || !data) return <div className='px-5 pt-6 text-xs text-red-600'>Could not load the settings.</div>;

	// Keyed so a brand switch starts from that brand's saved text, not the previous brand's draft.
	return <DisclaimerSettings key={data.buId ?? 'account'} settings={data} />;
};

/**
 * Edits are a local draft until the one Save button sends every part in a single PUT, so the
 * disclaimer, subtitle and starters are always saved together and nothing is half written.
 */
const DisclaimerSettings = ({ settings }: { settings: SalesAgentSettings }) => {
	const textId = useId();
	const subtitleId = useId();
	const save = useSaveSalesAgentSettings();
	// What the server holds, as last seen: the draft is dirty when its cleaned values differ.
	const [baseline, setBaseline] = useState(settings);
	const [draft, setDraft] = useState(() => toSettingsDraft(settings));
	const [error, setError] = useState<string | null>(null);
	const values = draftValues(draft);
	const isDirty = !sameValues(values, savedValues(baseline));

	// A refetch with newer settings (saved elsewhere) replaces the draft only if it is unedited.
	const [synced, setSynced] = useState(settings);
	if (synced !== settings) {
		setSynced(settings);
		if (!sameValues(savedValues(settings), savedValues(baseline))) {
			setBaseline(settings);
			if (!isDirty) setDraft(toSettingsDraft(settings));
		}
	}

	const update = (patch: Partial<SettingsDraft>) => setDraft((current) => ({ ...current, ...patch }));

	// Consent needs text, so the switch refuses to turn on over an empty field.
	const onToggle = (checked: boolean) => {
		if (checked && draft.disclaimerText.trim() === '') {
			setError(NEEDS_TEXT_MESSAGE);
			return;
		}
		setError(null);
		update({ disclaimerEnabled: checked });
	};

	const onSave = () => {
		if (values.disclaimer.enabled && values.disclaimer.text === '') {
			setError(NEEDS_TEXT_MESSAGE);
			return;
		}
		setError(null);
		const sent = draft;
		save.mutate(values, {
			onSuccess: (saved) => {
				setBaseline(saved);
				// Only a draft untouched since Save is reset; edits made while saving stay.
				setDraft((current) => (current === sent ? toSettingsDraft(saved) : current));
				toast.success('Settings saved');
			},
			onError: (err) => toast.error(getApiErrorMessage(err, 'Could not save the settings'))
		});
	};

	return (
		<div className='flex flex-col items-start px-5 pt-6 pb-6'>
			<div className='flex w-[600px] max-w-full flex-col gap-6'>
				<section className='flex w-full flex-col overflow-hidden rounded-2xl border border-gray-500 bg-white'>
					<div className='flex w-full items-center gap-6 border-b border-gray-200 px-4 py-3.5'>
						<div className='flex min-w-0 flex-1 flex-col gap-1.5'>
							<h2 className='text-sm font-medium text-ink'>Performance</h2>
							<p className='text-xs leading-4 text-gray-800'>Automatically sync orders for Performance reporting</p>
						</div>
						<Switch
							checked={draft.performanceEnabled}
							onCheckedChange={(performanceEnabled) => update({ performanceEnabled })}
							aria-label='Automatically sync orders for Performance reporting'
						/>
					</div>
					<p className='bg-gray-50 px-4 py-2.5 text-xs text-gray-800'>
						Connect Shopify to keep your order and revenue metrics up to date.
					</p>
				</section>
				{/* Figma's #eceef1 rule and #f7f8fa band have no token: gray-200 (#edeff2) and gray-50 (#f9f9f9) are the nearest. */}
				<section className='flex w-full flex-col overflow-hidden rounded-2xl border border-gray-500 bg-white'>
					<div className='flex w-full items-center gap-6 border-b border-gray-200 px-4 py-3.5'>
						<div className='flex min-w-0 flex-1 flex-col gap-1.5'>
							<h2 className='text-sm font-medium text-ink'>Disclaimer</h2>
							<p className='text-xs leading-4 text-gray-800'>Ask for consent before the chat starts</p>
						</div>
						<Switch
							checked={draft.disclaimerEnabled}
							onCheckedChange={onToggle}
							aria-label='Ask for consent before the chat starts'
						/>
					</div>
					<p className='bg-gray-50 px-4 py-2.5 text-xs text-gray-800'>
						Shoppers must agree to the disclaimer before they can send a message. Turn off to show it only in
						the chat footer.
					</p>
				</section>

				<SettingsFieldCard
					title='Disclaimer text'
					label='Text shown to shopper in the chat'
					labelFor={textId}
					infoAlt='About the disclaimer text'
					info='Shown in the consent dialog, and from the disclaimer link in the chat footer.'
				>
					<DisclaimerTextField
						id={textId}
						value={draft.disclaimerText}
						onChange={(next) => {
							update({ disclaimerText: next });
							if (error && next.trim() !== '') setError(null);
						}}
					/>
					{error && <p className='text-xs leading-4 text-red-600'>{error}</p>}
					{settings.inherited && (
						<p className='text-xs leading-4 text-gray-800'>
							This brand uses the account&apos;s disclaimer. Saving here gives it its own.
						</p>
					)}
				</SettingsFieldCard>

				<SettingsFieldCard title='Subtitle' label={SUBTITLE_HELP} labelFor={subtitleId}>
					<Input
						id={subtitleId}
						aria-label='Subtitle'
						value={draft.subtitle}
						maxLength={SUBTITLE_MAX_LENGTH}
						placeholder={DEFAULT_SUBTITLE}
						onChange={(event) => update({ subtitle: event.target.value })}
						className='h-10 rounded-2xl border-gray-500 py-0 text-xs leading-4 text-ink placeholder:text-gray-800'
					/>
				</SettingsFieldCard>

				<StarterQuestionsSettings starters={draft.starters} onChange={(starters) => update({ starters })} />

				<div className='flex w-full justify-end'>
					<Button
						type='button'
						disabled={!isDirty || save.isPending}
						onClick={onSave}
						className={cn(PILL_BUTTON, 'px-3')}
					>
						{save.isPending && <Loader2 className='size-3.5 animate-spin' />}
						Save
					</Button>
				</div>
			</div>
		</div>
	);
};
